/**
 * @file AdminUserDetail.jsx — /cx/usuario/:id
 */
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, LogIn, User as UserIcon } from "lucide-react";
import { useAdminUserDetail } from "../hooks/useAdminUserDetail";
import { adminService } from "../services/admin";
import { useAuth } from "../hooks/useAuth";
import { ADMIN_STRINGS, APPOINTMENT_STRINGS } from "../constants/strings";
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  Chip,
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  Select,
  SelectItem,
  Spinner,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  toast,
} from "../ui";
import "./AdminGeneric.css";
import "./AdminUserDetail.css";

const ROLES = ["PACIENTE", "PROFESIONAL", "ADMIN"];

const STATUS_TONES = {
  AGENDADA: "warn",
  CONFIRMADA: "ok",
  COMPLETADA: "primary",
  CANCELADA: "neutral",
  NO_ASISTIO: "err",
};

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const { user, isLoading, refresh, deleteUser, impersonate } = useAdminUserDetail(parseInt(id));
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSelf = user?.id === me?.id;

  const handleRoleChange = async (newRole) => {
    try {
      await adminService.updateRole(user.id, newRole);
      toast.success("Rol actualizado");
      refresh();
    } catch {
      // ignore
    }
  };

  const handleToggleActive = async () => {
    try {
      await adminService.toggleActive(user.id);
      toast.success(user.isActive ? "Usuario desactivado" : "Usuario activado");
      refresh();
    } catch {
      // ignore
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteUser();
      navigate("/cx/usuarios");
    } catch {
      setDeleting(false);
    }
  };

  const handleImpersonate = async () => {
    try {
      await impersonate();
      window.location.href = "/";
    } catch {
      // ignore
    }
  };

  if (isLoading) {
    return (
      <div className="ag-page">
        <div className="ag-loading">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="ag-page">
        <Button variant="ghost" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate("/cx/usuarios")}>
          Volver
        </Button>
        <Card className="ag-empty">
          <p>Usuario no encontrado</p>
        </Card>
      </div>
    );
  }

  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();

  return (
    <div className="ag-page">
      <Button
        variant="ghost"
        leftIcon={<ArrowLeft size={14} />}
        onClick={() => navigate("/cx/usuarios")}
        className="aud-back"
      >
        Volver a usuarios
      </Button>

      {/* Profile card */}
      <Card>
        <div className="aud-profile">
          <Avatar size="lg" initials={initials} alt={`${user.firstName} ${user.lastName}`} />
          <div className="aud-profile-meta">
            <h1 className="aud-name">{user.firstName} {user.lastName}</h1>
            <div className="aud-profile-contact">
              {user.email && <span>{user.email}</span>}
              {user.phone && <span>{user.phone}</span>}
            </div>
            <div className="aud-profile-chips">
              <Chip tone="primary" size="sm">
                {ADMIN_STRINGS.ROLE_LABELS[user.role] || user.role}
              </Chip>
              <Chip tone={user.isActive ? "ok" : "neutral"} size="sm">
                {user.isActive ? ADMIN_STRINGS.ACTIVE : ADMIN_STRINGS.INACTIVE}
              </Chip>
              {user.isEmailVerified && (
                <Chip tone="primary" size="sm">Email verificado</Chip>
              )}
              {user.isPhoneVerified && (
                <Chip tone="primary" size="sm">Teléfono verificado</Chip>
              )}
            </div>
          </div>
        </div>

        <div className="aud-meta-grid">
          <div className="aud-meta-cell">
            <span className="aud-meta-label">Registro</span>
            <span className="aud-meta-value">
              {new Date(user.createdAt).toLocaleDateString("es-MX")}
            </span>
          </div>
          <div className="aud-meta-cell">
            <span className="aud-meta-label">Proveedor auth</span>
            <span className="aud-meta-value">{user.authProvider || "EMAIL"}</span>
          </div>
        </div>

        <div className="aud-actions">
          <Select
            value={user.role}
            onValueChange={handleRoleChange}
            disabled={isSelf}
          >
            {ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {ADMIN_STRINGS.ROLE_LABELS[r]}
              </SelectItem>
            ))}
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={isSelf}
            onClick={handleToggleActive}
          >
            {user.isActive ? ADMIN_STRINGS.DEACTIVATE : ADMIN_STRINGS.ACTIVATE}
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<LogIn size={14} />}
            disabled={isSelf}
            onClick={handleImpersonate}
          >
            {ADMIN_STRINGS.IMPERSONATE}
          </Button>
          <Button
            variant="danger"
            size="sm"
            leftIcon={<Trash2 size={14} />}
            disabled={isSelf}
            onClick={() => setDeleteOpen(true)}
          >
            {ADMIN_STRINGS.DELETE_USER}
          </Button>
        </div>
      </Card>

      {user.professional && (
        <Card>
          <CardHeader title="Perfil profesional" />
          <div className="aud-meta-grid">
            <div className="aud-meta-cell">
              <span className="aud-meta-label">Especialidad</span>
              <span className="aud-meta-value">{user.professional.specialty || "—"}</span>
            </div>
            <div className="aud-meta-cell">
              <span className="aud-meta-label">Slug</span>
              <span className="aud-meta-value">{user.professional.slug || "—"}</span>
            </div>
            <div className="aud-meta-cell">
              <span className="aud-meta-label">Onboarding</span>
              <Chip tone={user.professional.onboardingComplete ? "ok" : "warn"} size="sm">
                {user.professional.onboardingComplete
                  ? "Completo"
                  : `Paso ${user.professional.onboardingStep}`}
              </Chip>
            </div>
            <div className="aud-meta-cell">
              <span className="aud-meta-label">Ubicaciones</span>
              <span className="aud-meta-value">{user.professional.locations?.length || 0}</span>
            </div>
          </div>
          {user.professional.specialties?.length > 0 && (
            <div className="aud-cedulas">
              <span className="aud-meta-label">Cédulas</span>
              <div className="aud-cedulas-list">
                {user.professional.specialties.map((s) => (
                  <Chip key={s.id} size="sm">
                    {s.name}{s.cedula ? ` (${s.cedula})` : ""}
                  </Chip>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {user.patient && (
        <Card>
          <CardHeader title="Perfil paciente" />
          <div className="ag-key-row">
            <span className="ag-key-label">Fecha de nacimiento</span>
            <span className="ag-key-value">
              {user.patient.dateOfBirth
                ? new Date(user.patient.dateOfBirth).toLocaleDateString("es-MX")
                : "—"}
            </span>
          </div>
        </Card>
      )}

      {user.appointments?.length > 0 && (
        <Card padded={false}>
          <div className="aud-section-head">
            <h3 className="ag-section-title">Citas ({user.appointments.length})</h3>
          </div>
          <Table>
            <THead>
              <TR>
                <TH>Fecha</TH>
                <TH>Profesional</TH>
                <TH>Paciente</TH>
                <TH>Estado</TH>
              </TR>
            </THead>
            <TBody>
              {user.appointments.map((a) => (
                <TR key={a.id}>
                  <TD>
                    <span className="ag-date">
                      {new Date(a.dateTime).toLocaleString("es-MX", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </span>
                  </TD>
                  <TD>
                    {a.professional?.user?.firstName} {a.professional?.user?.lastName}
                  </TD>
                  <TD>
                    {a.patient?.user?.firstName} {a.patient?.user?.lastName}
                  </TD>
                  <TD>
                    <Chip tone={STATUS_TONES[a.status] || "neutral"} size="sm">
                      {APPOINTMENT_STRINGS?.STATUS_LABELS?.[a.status] || a.status}
                    </Chip>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {user.auditTrail?.length > 0 && (
        <Card padded={false}>
          <div className="aud-section-head">
            <h3 className="ag-section-title">Auditoría ({user.auditTrail.length})</h3>
          </div>
          <Table>
            <THead>
              <TR>
                <TH>Fecha</TH>
                <TH>Acción</TH>
                <TH>Detalles</TH>
              </TR>
            </THead>
            <TBody>
              {user.auditTrail.map((a) => (
                <TR key={a.id}>
                  <TD>
                    <span className="ag-date">
                      {new Date(a.createdAt).toLocaleString("es-MX", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </span>
                  </TD>
                  <TD>{ADMIN_STRINGS.AUDIT_ACTIONS[a.action] || a.action}</TD>
                  <TD>
                    <span className="ag-truncate">
                      {a.metadata ? JSON.stringify(a.metadata) : "—"}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent
          size="sm"
          title={ADMIN_STRINGS.DELETE_CONFIRM_TITLE}
          description={ADMIN_STRINGS.DELETE_CONFIRM_MSG}
        >
          <DialogBody>
            <p style={{ fontWeight: 600, color: "var(--ink)", margin: 0 }}>
              {user.firstName} {user.lastName} ({user.email || user.phone})
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
